// =========================================================================
// SINGLE SOURCE for GTM / server-side-GTM advice.
// =========================================================================
// The same recommendation is rendered by the dashboard panel
// (W3CPerformanceInsights) AND the standalone HTML export
// (diagnosticsExportStyles). Historically each had its own copy of the
// detection + wording, fixes landed in one and the client saw contradictory
// advice ("deploy sGTM" vs. "finish the sGTM migration") in a single report.
// Both surfaces MUST import detection and wording from here — do not inline
// new GTM copy elsewhere.

// sGTM detection scans the WHOLE diagnostics payload, never just the top-10
// "slowest resources" — the sGTM endpoint drops in and out of that list
// between runs, which made the advice flip-flop.
export const detectSgtm = (diagnostics) => {
  try {
    return /server-side-tagging|\.run\.app\//i.test(JSON.stringify(diagnostics || {}));
  } catch {
    return false; // circular or huge payload
  }
};

// A destination or gtag load on this hostname is not a GTM container.
export const hasGtmContainer = (diagnostics) => {
  try {
    return /googletagmanager\.com\/gtm\.js\?[^"'\s]*\bid=GTM-[A-Z0-9]+/i.test(JSON.stringify(diagnostics || {}));
  } catch {
    return false;
  }
};

export const scopeTagRecommendations = (recommendations, diagnostics) => {
  if (!Array.isArray(recommendations)) return [];
  if (hasGtmContainer(diagnostics)) return recommendations;
  return recommendations.filter(rec => ![
    'gtm-server-side', 'gtm-consolidate-containers', 'remove-unused-gtm-tags'
  ].includes(rec?.id));
};

// The advice clause both surfaces append to their context sentence.
export const gtmAdviceSentence = (sgtmDetected, lang = 'cs') => {
  if (sgtmDetected) {
    // Endpoint presence does not prove which tags have migrated or remain.
    return lang === 'cs'
      ? 'Je vidět možný server-side tagging endpoint, ale stav migrace tagů nelze z tohoto měření určit. Prověřte konfiguraci a případné vhodné přesuny; přínos ověřte A/B laboratorním měřením.'
      : 'A possible server-side tagging endpoint is visible, but this measurement cannot establish tag migration status. Review the configuration and any suitable changes; verify the benefit with an A/B lab measurement.';
  }
  // Candidate phrasing, not a guaranteed fix: server-side GTM is not an
  // automatic replacement for client-side GTM — the move alone does not
  // remove browser-side GTM work; the benefit depends on the concrete tags
  // and measurement architecture and must be verified by an A/B lab run.
  return lang === 'cs'
    ? 'Prověřte duplicitní načítání Google tagů a možnost přesunout vhodné měřicí/marketingové operace na server-side infrastrukturu — potenciální úsporu ověřte A/B laboratorním měřením, samotný přesun ji negarantuje.'
    : 'Audit duplicate Google tag loading and consider moving suitable measurement/marketing operations to server-side infrastructure — verify the potential saving with an A/B lab measurement; the move alone does not guarantee it.';
};

// Endpoint detection alone is not evidence of an ongoing migration.
export const gtmMigrationTitle = (lang = 'cs') => (lang === 'cs'
  ? 'Prověřit konfiguraci server-side tagování'
  : 'Review server-side tagging configuration');
